import { useEffect, useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AppShellAdmin, Button, useThemeMode } from "@atlas/ui/web";
import { papelLibera, type Papel } from "@atlas/types";
import { atlas } from "../../lib/sdk";
import { AtlasBrand } from "../../components/AtlasBrand";
import { BotaoManual } from "../../components/BotaoManual";
import { podeAcessar, readPrefeituraPermissoesFromJwt,
  readPrefeituraSenhaTemporariaFromJwt } from "../../lib/prefeitura-perms";
import { UnidadeAtivaProvider, rotuloDaUnidade, useUnidadeAtiva } from "../../lib/unidade-ativa";

const NAV = [
  { key: "dashboard", label: "Painel", href: "/prefeitura/dashboard", icon: "◉" },
  { key: "servidores", label: "Servidores", href: "/prefeitura/servidores", icon: "◎" },
  // Cliente pediu 31/07/2026 desativar a aba temporariamente. Rota /prefeitura/convenios
  // continua existindo pra links diretos (nao apagamos a pagina) — so some do menu.
  // { key: "convenios", label: "Regras de Convênio", href: "/prefeitura/convenios", icon: "▣" },
  { key: "contratos", label: "Contratos", href: "/prefeitura/contratos", icon: "▤" },
  { key: "adf", label: "ADF / Descontos", href: "/prefeitura/adf", icon: "▧" },
  { key: "troca-arquivos", label: "Troca de arquivos", href: "/prefeitura/troca-arquivos", icon: "⇄" },
  { key: "relatorios", label: "Relatórios", href: "/prefeitura/relatorios", icon: "▨" },
  // #279: o repasse aos credores — o papel `responsavel_repasse` chega aqui sem passar pela troca de arquivos.
  { key: "repasses", label: "Repasses", href: "/prefeitura/repasses", icon: "⇢" },
  { key: "anuencia", label: "Anuência de dados", href: "/prefeitura/anuencia", icon: "✓" },
  { key: "perfis", label: "Usuários e acessos", href: "/prefeitura/perfis", icon: "◐" },
  { key: "conta", label: "Minha conta", href: "/prefeitura/conta", icon: "◐" },
];

/**
 * O provider fica FORA do shell para que o `Outlet` — as páginas — enxergue a
 * mesma unidade ativa que o seletor do cabeçalho escreve. Um provider por
 * página faria cada tela ter a sua, e trocar de organização mudaria o título
 * sem mudar os números.
 */
export function PrefeituraLayout() {
  return (
    <UnidadeAtivaProvider>
      <PrefeituraShell />
    </UnidadeAtivaProvider>
  );
}

function PrefeituraShell() {
  const nav = useNavigate();
  const location = useLocation();
  const { resolved, setMode } = useThemeMode();
  const activeKey = location.pathname.split("/")[2] ?? "dashboard";
  const me = useQuery({ queryKey: ["prefeitura", "me"], queryFn: () => atlas.prefeitura.me() });

  // Permissoes granulares do sub-usuario (claim prefeitura_permissoes). null =
  // login da prefeitura-organizacao -> ve tudo (retrocompat).
  const [permissoes, setPermissoes] = useState<string[] | null>(null);
  useEffect(() => {
    try {
      const stored = window.localStorage.getItem("atlas:tokens");
      const token = stored ? JSON.parse(stored)?.access_token : null;
      setPermissoes(readPrefeituraPermissoesFromJwt(token));
      // Senha temporaria: a API recusa TODAS as rotas do portal com 403
      // `senha_temporaria_pendente`, entao sem este desvio o sub-usuario veria um
      // portal em que nada carrega. Quem barra e' o servidor; isto so' o leva
      // para a unica tela que resolve.
      if (readPrefeituraSenhaTemporariaFromJwt(token) && !location.pathname.endsWith("/trocar-senha")) {
        nav("/prefeitura/trocar-senha", { replace: true });
      }
    } catch { setPermissoes(null); }
  }, [location.pathname, nav]);

  // Redireciona quem tenta abrir uma URL fora do escopo das permissoes — cai no
  // primeiro item permitido do menu (evita loop se nao tiver "conta").
  useEffect(() => {
    if (!permissoes || permissoes.includes("*")) return;
    if (!podeAcessar(permissoes, activeKey)) {
      const first = NAV.find((i) => podeAcessar(permissoes, i.key));
      if (first) nav(first.href, { replace: true });
    }
  }, [permissoes, activeKey, nav]);

  const { unidade, unidades, temAtendidas, escolher } = useUnidadeAtiva();

  /**
   * O menu, com os dois recortes — e eles são diferentes de propósito.
   *
   * A PERMISSÃO REMOVE o item: aquele recurso não é daquela pessoa em lugar
   * nenhum, e mostrá-lo só provocaria um pedido que ninguém pode atender.
   *
   * O PAPEL DESABILITA: o recurso é dela na própria organização e não naquela
   * que ela está operando agora. Sumir faria o menu mudar de tamanho ao trocar
   * de unidade, e o operador procuraria o item que "desapareceu" — com o
   * motivo no hover, ele vê que existe e por que não alcança.
   *
   * `perfis` e `conta` nunca são desabilitados: operam sempre a organização de
   * ORIGEM, e por isso não dependem da unidade ativa.
   */
  const filteredNav = useMemo(() => {
    const papeis = (unidade?.papeis ?? []) as Papel[];
    const naOrigem = unidade?.origem !== false;
    return NAV.filter((item) => podeAcessar(permissoes, item.key)).map((item) => {
      if (naOrigem || item.key === "perfis" || item.key === "conta") return item;
      if (papelLibera(papeis, item.key, "leitura")) return item;
      return {
        ...item,
        disabled: true,
        title: `Não liberado para ${unidade?.sigla || unidade?.nome}: os papéis desta relação não alcançam ${item.label.toLowerCase()}.`,
      };
    });
  }, [permissoes, unidade]);

  // O rótulo e o nome com que a organização se apresenta. Fallback para o
  // comportamento antigo enquanto a resposta de `/me` não trouxer os campos
  // novos — Pages e Workers deployam separados, então um bundle web novo pode
  // rodar contra a API anterior por alguns minutos.
  const rotuloTipo = me.data?.prefeitura.rotuloTipo ?? "Prefeitura";
  const nomeOrg = me.data?.prefeitura.nomeExibicao ?? me.data?.prefeitura.nome ?? "";

  return (
    <AppShellAdmin
      // Feature 009: o portal serve QUALQUER órgão ou entidade. Antes ele dizia
      // "Prefeitura" em três lugares fixos — para os usuários de uma câmara ou de
      // uma autarquia, era o produto insistindo que eles eram outra coisa. Agora
      // o rótulo vem do tipo da própria organização, e o nome é o de exibição
      // (que fora do tipo prefeitura prefere a sigla).
      brand={<AtlasBrand sub={rotuloTipo} />}
      sidebarFooter={<BotaoManual baixar={() => atlas.prefeitura.manualBlob()} rotulo="Manual de uso" />}
      convenioSlot={
        me.data ? (
          <div style={{ padding: "10px 12px", borderRadius: 10, background: "var(--bg-elev-2)", fontSize: 13 }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".06em", color: "var(--text-dim)", textTransform: "uppercase" }}>{rotuloTipo}</div>
            <div style={{ fontWeight: 700, marginTop: 2 }}>{nomeOrg}/{me.data.prefeitura.uf}</div>
          </div>
        ) : null
      }
      topbarSlot={
        <>
          <div style={{ fontSize: 13, color: "var(--text-muted)" }}>Portal do órgão ou entidade</div>
          {/* O SELETOR SÓ EXISTE QUANDO HÁ ESCOLHA. Um seletor de uma opção só é
              ruído que sugere uma decisão inexistente — e, pior, faria todo
              login de organização sem atendidas parecer ter algo a configurar. */}
          {temAtendidas ? (
            /*
             * INLINE, e não `SelectField`.
             *
             * `SelectField` é campo de FORMULÁRIO: o `FieldShell` empilha o
             * rótulo ACIMA do controle, contando com a altura que um formulário
             * tem. A topbar é uma faixa baixa com `align-items: center`, e ali a
             * pilha não cabe — o "Operando" ficava espremido a ponto de não se
             * ler, que foi como o usuário encontrou (08/09/2026).
             *
             * O `<label>` ENVOLVE o `<select>`: a associação é implícita, sem
             * precisar de `id`, e `getByLabelText(/^Operando/)` continua
             * achando — é como os testes do seletor perguntam por ele.
             */
            <label
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                fontSize: 13,
                minWidth: 260,
              }}
            >
              <span style={{ color: "var(--text-dim)", whiteSpace: "nowrap", fontWeight: 600 }}>
                Operando
              </span>
              <select
                value={String(unidade?.id ?? "")}
                onChange={(e) => escolher(Number(e.target.value))}
                style={{
                  flex: 1,
                  minWidth: 0,
                  padding: "6px 10px",
                  borderRadius: 8,
                  border: "1px solid var(--border)",
                  background: "var(--bg-elev-2)",
                  color: "var(--text)",
                  fontSize: 13,
                }}
              >
                {unidades.map((u) => (
                  <option key={u.id} value={String(u.id)}>
                    {rotuloDaUnidade(u)}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            {me.data ? (
              <span style={{ fontSize: 13, color: "var(--text)", fontWeight: 600 }} title="Organização logada">
                {nomeOrg}/{me.data.prefeitura.uf}
              </span>
            ) : null}
            <Button variant="ghost" size="sm" onClick={() => setMode(resolved === "dark" ? "light" : "dark")}>
              {resolved === "dark" ? "Tema claro" : "Tema escuro"}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={async () => {
                await atlas.logout().catch(() => undefined);
                window.localStorage.removeItem("atlas:role");
                window.localStorage.removeItem("atlas:tokens");
                nav("/login");
              }}
            >
              Sair
            </Button>
          </div>
        </>
      }
      nav={filteredNav}
      activeKey={activeKey}
      onNavigate={(item) => {
        if (item.href) nav(item.href);
      }}
    >
      <Outlet />
    </AppShellAdmin>
  );
}
